import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { betbyApi } from "@/betby/api/services";
import type { BetbyInitInfo } from "@/betby/api/types";
import { authManager } from "@/betby/models/auth/authInitializationService";
import { resolveSessionTicket } from "@/betby/models/auth/authSessionResolver";
import {
  cacheSessionTicket,
  isSessionTicketExpired,
  requestNewSessionTicket,
} from "@/betby/models/auth/sessionTicketCache";
import {
  sendSdkReadyMessage,
  sendTokenExpiredMessage,
} from "@/betby/models/host/unityMessageService";
import { useNavStore } from "@/betby/store/navStore";
import LogUtil from "@/betby/utils/LogUtil";
import analyticsBridge from "@/betby/adapters/analytics/analyticsBridge";
import {
  TRACK_EVENT_SDK_START_INTERFACE_SPM,
  TRACK_EVENT_SDK_START_INTERFACE_TIME_SPM,
  TRACK_EVENT_SDK_START_TIME_SPM,
} from "@/betby/utils/TrackEvents";

const GENERIC_SPORTS_ERROR_MESSAGE =
  "We're sorry, an error occurred. Please try re-entering the sports play later.";
const MIN_LOADING_TIME_MS = 1500;

// Global flag to track if first init has completed (persists across navigation)
let hasGlobalCompletedFirstInit = false;

type BalanceRequestStatus = "ok" | "network" | "kicked" | "error";

interface UseBetbyInitFlowParams {
  isAuthenticated: boolean;
  accessToken: string | null;
  forceLoginNeeded: boolean;
  isRendererInitializedRef: MutableRefObject<boolean>;
  startRedPointPolling: () => void;
  setInitInfoIfChanged: (nextInfo: BetbyInitInfo) => void;
  requestBalanceAndUpdateStore: (
    skipGlobalErrorHandler?: boolean,
  ) => Promise<BalanceRequestStatus>;
  showRetryModal: (onConfirm: () => void) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolveInitInfoFromResponse: (
    responseData: any,
    mode: string,
  ) => BetbyInitInfo | null;
}

/**
 * 统一管理 Betby 启动期的数据拉取流程：
 * - 并行请求 init/info 与 balance
 * - 管理 loading 的最短展示时间与进入 SDK 的时机
 * - 处理网络重试、401 续期、互踢等异常分支
 */
export function useBetbyInitFlow({
  isAuthenticated,
  accessToken,
  forceLoginNeeded,
  isRendererInitializedRef,
  startRedPointPolling,
  setInitInfoIfChanged,
  requestBalanceAndUpdateStore,
  showRetryModal,
  resolveInitInfoFromResponse,
}: UseBetbyInitFlowParams) {
  // 仅用于驱动启动页遮罩，真实业务状态由 refs 管理，避免频繁重渲染。
  // 如果已经完成过首次初始化，不再显示 loading
  const [isLoading, setIsLoading] = useState(
    () => !hasGlobalCompletedFirstInit,
  );
  // 两路请求（info + balance）均成功后，SDK 才允许结束启动阶段。
  const initPhaseBothSucceededRef = useRef(false);
  const initPhaseInfoSucceededRef = useRef(false);
  // 启动阶段计时与一次性埋点状态。
  const initStartTimeRef = useRef(0);
  const hasReportedStartTimeRef = useRef(false);
  const hasCompletedFirstInitRef = useRef(hasGlobalCompletedFirstInit);
  // 并发保护：只接受当前最新请求/流程结果，避免旧响应回写覆盖。
  const initInfoRequestSeqRef = useRef(0);
  const initFlowSeqRef = useRef(0);

  const fetchWalletBalanceRef = useRef<
    ((isPageStart?: boolean) => Promise<boolean>) | null
  >(null);
  const fetchBetbyInitInfoRef = useRef<
    ((isPageStart?: boolean) => Promise<boolean>) | null
  >(null);

  // 单点收口：结束 loading（满足最小展示时长）并可选启动红点轮询。
  const closeLoadingAndStartPolling = useCallback(
    (options?: { startPolling?: boolean }) => {
      const startPolling = options?.startPolling !== false;
      const isPageStart = !hasCompletedFirstInitRef.current;

      // 只有首次进入才需要等待最小展示时长，再次进入直接关闭
      const remainingTime = isPageStart
        ? Math.max(
            0,
            MIN_LOADING_TIME_MS - (Date.now() - initStartTimeRef.current),
          )
        : 0;

      setTimeout(() => {
        const loadDurationMs = Math.max(
          Date.now() - initStartTimeRef.current,
          0,
        );
        if (
          !hasReportedStartTimeRef.current &&
          !hasCompletedFirstInitRef.current
        ) {
          hasCompletedFirstInitRef.current = true;
          hasGlobalCompletedFirstInit = true; // Mark global first init completed
          hasReportedStartTimeRef.current = true;
          analyticsBridge.track(TRACK_EVENT_SDK_START_TIME_SPM, {
            src: 516,
            eb_key1: loadDurationMs,
            load_duration_ms: loadDurationMs,
          });
        }

        setIsLoading(false);
        // 向父页面发送 SDK 初始化成功消息
        sendSdkReadyMessage({ success: true });
        if (startPolling) startRedPointPolling();
      }, remainingTime);
    },
    [startRedPointPolling],
  );

  const getIsPageStart = useCallback(
    () => !hasCompletedFirstInitRef.current,
    [],
  );

  // 余额重试成功后，若 info 已就绪且 SDK 已初始化，立即允许进入主界面。
  const runBalanceRetryAndMaybeEnterSdk = useCallback(async () => {
    const ok = await fetchWalletBalanceRef.current?.();
    if (!ok) return;

    initPhaseBothSucceededRef.current = true;
    if (initPhaseInfoSucceededRef.current && isRendererInitializedRef.current) {
      closeLoadingAndStartPolling();
    }
  }, [closeLoadingAndStartPolling, isRendererInitializedRef]);

  const fetchWalletBalance = useCallback(
    async (isPageStart = true): Promise<boolean> => {
      const requestStartTime = Date.now();
      let status: "suc" | "fail" = "fail";

      if (isPageStart) {
        analyticsBridge.track(TRACK_EVENT_SDK_START_INTERFACE_SPM, {
          src: 516,
          eb_key1: "/balance",
        });
      }

      const balanceStatus = await requestBalanceAndUpdateStore(true);
      status = balanceStatus === "ok" ? "suc" : "fail";

      if (isPageStart) {
        const loadDurationMs = Math.max(Date.now() - requestStartTime, 0);
        analyticsBridge.track(TRACK_EVENT_SDK_START_INTERFACE_TIME_SPM, {
          src: 516,
          eb_key1: "/balance",
          eb_key2: status,
          time: loadDurationMs,
        });
      }

      if (balanceStatus === "ok") return true;
      if (balanceStatus === "kicked" || balanceStatus === "error") return false;

      if (balanceStatus === "network") {
        LogUtil.error(
          "[BetbyRenderer] 获取余额异常 (网络异常或 code -1)，弹出网络重试弹窗",
        );
        showRetryModal(() => void runBalanceRetryAndMaybeEnterSdk());
      }
      return false;
    },
    [
      requestBalanceAndUpdateStore,
      runBalanceRetryAndMaybeEnterSdk,
      showRetryModal,
    ],
  );

  useEffect(() => {
    fetchWalletBalanceRef.current = fetchWalletBalance;
  }, [fetchWalletBalance]);

  // init/info 的重试入口：与 balance 并行重拉，成功后统一推进初始化阶段。
  const runInitInfoRetryAndMaybeEnterSdk = useCallback(async () => {
    const [infoOk, balanceOk] = await Promise.all([
      fetchBetbyInitInfoRef.current
        ? fetchBetbyInitInfoRef.current(true)
        : Promise.resolve(false),
      fetchWalletBalanceRef.current
        ? fetchWalletBalanceRef.current(true)
        : Promise.resolve(false),
    ]);

    initPhaseInfoSucceededRef.current = !!infoOk;
    initPhaseBothSucceededRef.current = !!infoOk && !!balanceOk;

    if (infoOk && balanceOk && isRendererInitializedRef.current) {
      closeLoadingAndStartPolling();
    }
  }, [closeLoadingAndStartPolling, isRendererInitializedRef]);

  const fetchBetbyInitInfo = useCallback(
    async (isPageStart = true): Promise<boolean> => {
      const requestSeq = ++initInfoRequestSeqRef.current;
      const requestStartTime = Date.now();
      let hasTrackedInterfaceDuration = false;
      const isStaleRequest = () => requestSeq !== initInfoRequestSeqRef.current;

      const trackInterfaceDuration = (status: "suc" | "fail") => {
        if (!isPageStart || hasTrackedInterfaceDuration) return;
        hasTrackedInterfaceDuration = true;
        const loadDurationMs = Math.max(Date.now() - requestStartTime, 0);
        analyticsBridge.track(TRACK_EVENT_SDK_START_INTERFACE_TIME_SPM, {
          src: 516,
          eb_key1: "/init/info",
          eb_key2: status,
          time: loadDurationMs,
        });
      };

      // 仅在当前请求仍是最新时才写入 initInfo，避免货币切换导致的脏写。
      const handleSuccess = (responseData: unknown): boolean => {
        if (isStaleRequest()) {
          return false;
        }

        const modeNow = useNavStore.getState().currentMode;
        const matchedInfo = resolveInitInfoFromResponse(responseData, modeNow);
        if (!matchedInfo) return false;

        setInitInfoIfChanged(matchedInfo);
        return true;
      };

      try {
        if (isPageStart) {
          analyticsBridge.track(TRACK_EVENT_SDK_START_INTERFACE_SPM, {
            src: 516,
            eb_key1: "/init/info",
          });
        }

        const response = await betbyApi.getInitInfo({
          options: {
            skipGlobalErrorHandler: true,
            showUnrecognizedErrorModal: true,
          },
        });

        trackInterfaceDuration(
          response.code === 200 && response.data?.bet_by_init_info
            ? "suc"
            : "fail",
        );

        if (response.code === 200 && response.data?.bet_by_init_info) {
          return handleSuccess(response.data);
        }

        if (response.code === -1) {
          LogUtil.error(
            "[BetbyRenderer] 获取初始化信息异常 (code -1):",
            response.data,
          );
          showRetryModal(() => void runInitInfoRetryAndMaybeEnterSdk());
          return false;
        }

        const isTokenExpired = response.code === 401;
        const isKickedOut = [4011, 4012, 4013, 4014, 4015].includes(
          response.code,
        );

        if (isKickedOut) {
          LogUtil.warn(
            `[BetbyRenderer] 捕获互踢错误码 ${response.code}，不进行自动重试，直接弹窗`,
          );
          sendSdkReadyMessage({
            success: false,
            error: "Kicked out",
            code: response.code,
          });
          sendTokenExpiredMessage();
          return false;
        }

        if (isTokenExpired) {
          LogUtil.warn(
            `[BetbyRenderer] 初始化信息获取失败 (Code: ${response.code})，检查 sessionTicket 状态...`,
          );

          // Check if session ticket is expired
          if (isSessionTicketExpired()) {
            LogUtil.info(
              "[BetbyRenderer] Session ticket expired, requesting new from host...",
            );

            // Try to request new session ticket from host
            const newTicket = await requestNewSessionTicket();
            if (newTicket) {
              LogUtil.info(
                "[BetbyRenderer] Got new session ticket, retrying login...",
              );
              // Try login with new ticket
              try {
                if (isPageStart) {
                  analyticsBridge.track(TRACK_EVENT_SDK_START_INTERFACE_SPM, {
                    src: 516,
                    eb_key1: "/login",
                  });
                }

                const loginRes = await authManager.login(newTicket);
                if (loginRes.code === 200 && loginRes.data?.access_token) {
                  // Retry init info with new session
                  const retryRes = await betbyApi.getInitInfo({
                    options: {
                      skipGlobalErrorHandler: true,
                      showUnrecognizedErrorModal: true,
                    },
                  });
                  if (
                    retryRes.code === 200 &&
                    retryRes.data?.bet_by_init_info
                  ) {
                    return handleSuccess(retryRes.data);
                  }
                }
              } catch (error) {
                LogUtil.error(
                  "[BetbyRenderer] Login with new session ticket failed:",
                  error,
                );
              }
            }
          }

          // Fallback: try existing session ticket
          const ticket = resolveSessionTicket();
          if (ticket) {
            // Cache the session ticket
            cacheSessionTicket(ticket);

            try {
              if (isPageStart) {
                analyticsBridge.track(TRACK_EVENT_SDK_START_INTERFACE_SPM, {
                  src: 516,
                  eb_key1: "/login",
                });
              }

              const loginRes = await authManager.login(ticket);
              if (loginRes.code === 200 && loginRes.data?.access_token) {
                // Retry init info
                const retryRes = await betbyApi.getInitInfo({
                  options: {
                    skipGlobalErrorHandler: true,
                    showUnrecognizedErrorModal: true,
                  },
                });
                if (
                  retryRes.code === 200 &&
                  retryRes.data?.bet_by_init_info
                ) {
                  return handleSuccess(retryRes.data);
                }
              }
            } catch (error) {
              LogUtil.error("[BetbyRenderer] 重新登录过程异常:", error);
            }
          } else {
            LogUtil.warn(
              "[BetbyRenderer] 未找到 sessionTicket，无法自动重新登录",
            );
          }

          LogUtil.error(
            "[BetbyRenderer] 重新登录/重试失败，向 Unity 发送 Token 过期消息",
          );
          sendSdkReadyMessage({
            success: false,
            error: "Token expired or login failed",
            code: response.code,
          });
          sendTokenExpiredMessage();
          return false;
        }

        LogUtil.error("[BetbyRenderer] 获取初始化信息失败:", {
          code: response.code,
          hasData: !!response.data,
          response,
        });
        sendSdkReadyMessage({
          success: false,
          error: GENERIC_SPORTS_ERROR_MESSAGE,
          code: response.code,
        });
        sendTokenExpiredMessage(GENERIC_SPORTS_ERROR_MESSAGE);
        return false;
      } catch (error) {
        trackInterfaceDuration("fail");
        LogUtil.error("[BetbyRenderer] 获取初始化信息异常:", error);
        showRetryModal(() => void runInitInfoRetryAndMaybeEnterSdk());
        return false;
      }
    },
    [
      resolveInitInfoFromResponse,
      runInitInfoRetryAndMaybeEnterSdk,
      setInitInfoIfChanged,
      showRetryModal,
    ],
  );

  useEffect(() => {
    fetchBetbyInitInfoRef.current = fetchBetbyInitInfo;
  }, [fetchBetbyInitInfo]);

  // 启动主流程：并行拉取 info/balance，并通过序号保护避免旧流程回写。
  const fetchInitInfo = useCallback(async () => {
    const flowSeq = ++initFlowSeqRef.current;
    if (!isAuthenticated || !accessToken || forceLoginNeeded) {
      return;
    }

    LogUtil.info("[BetbyRenderer] 开始获取 Betby 初始化信息和余额...");
    initStartTimeRef.current = Date.now();
    initPhaseBothSucceededRef.current = false;
    initPhaseInfoSucceededRef.current = false;

    const isPageStart = getIsPageStart();
    const [infoOk, balanceOk] = await Promise.all([
      fetchBetbyInitInfo(isPageStart),
      fetchWalletBalance(isPageStart),
    ]);

    if (flowSeq !== initFlowSeqRef.current) {
      return;
    }

    initPhaseInfoSucceededRef.current = infoOk;
    initPhaseBothSucceededRef.current = infoOk && balanceOk;
    if (infoOk && balanceOk && isRendererInitializedRef.current) {
      closeLoadingAndStartPolling();
    }
  }, [
    accessToken,
    closeLoadingAndStartPolling,
    fetchBetbyInitInfo,
    fetchWalletBalance,
    forceLoginNeeded,
    getIsPageStart,
    isAuthenticated,
    isRendererInitializedRef,
  ]);

  return {
    isLoading,
    setIsLoading,
    initStartTimeRef,
    initPhaseBothSucceededRef,
    getIsPageStart,
    closeLoadingAndStartPolling,
    fetchInitInfo,
  };
}
